import React, { useEffect, useState } from "react";
import { Magnet, Loader2 } from "lucide-react";
import api from "@/lib/api";
import TorrentList from "@/components/TorrentList";

export default function SearchTorrents({ q }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true); // auto-expand by default
  const [userToggled, setUserToggled] = useState(false);

  // Fetch as soon as a query is present — don't make users hunt for a "Show" button
  useEffect(() => {
    if (!q) return;
    let cancel = false;
    setData(null);
    setLoading(true);
    if (!userToggled) setOpen(true);
    (async () => {
      try {
        const { data } = await api.get("/torrents/search", { params: { q } });
        if (!cancel) setData(data);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [q]);

  if (!q) return null;

  const count = data?.count ?? 0;

  return (
    <section className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden mb-8" data-testid="search-torrents-section">
      <button
        onClick={() => { setOpen((o) => !o); setUserToggled(true); }}
        data-testid="search-torrents-toggle"
        className="w-full px-5 py-4 flex items-center gap-3 hover:bg-white/[0.03] transition-colors text-left"
      >
        <Magnet className="w-5 h-5 text-[var(--tt-primary)]" />
        <div className="flex-1">
          <div className="font-display font-bold text-base flex items-center gap-2 flex-wrap">
            <span>Torrent matches for &ldquo;{q}&rdquo;</span>
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--tt-text-muted)]" />}
          </div>
          <div className="text-[11px] text-[var(--tt-text-muted)] mt-0.5">
            Live results from YTS, EZTV, Knaben, 1337x, TorrentGalaxy &mdash; auto-indexed into our library
          </div>
        </div>
        {data && (
          <span
            className={`text-xs font-bold px-2 py-1 rounded ${
              count > 0 ? "bg-[var(--tt-yellow)] text-black" : "bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)]"
            }`}
            data-testid="search-torrents-count"
          >
            {count}
          </span>
        )}
        <span className="text-xs text-[var(--tt-primary)] font-semibold">
          {open ? "Hide" : "Show"}
        </span>
      </button>

      {open && (
        <div className="border-t border-[var(--tt-border)]">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-[var(--tt-text-muted)] py-8 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Querying torrent indexers&hellip;
            </div>
          ) : data && data.results?.length > 0 ? (
            <TorrentList
              title="Matches"
              rows={data.results}
              badgeColor="var(--tt-primary)"
              testId="search-torrents-list"
              mediaType="movie"
            />
          ) : (
            <div className="text-center text-[var(--tt-text-muted)] py-8 text-sm">
              No torrents matched this query.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
